import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { toast } from "sonner";
import "@/index.css";
import App from "@/App";
import { clearSession, setSession } from "@/lib/authSession";

const API_URL = process.env.REACT_APP_API_URL;
let refreshPromise = null;
const isDevelopment = process.env.NODE_ENV === "development";
const terminalAuthErrors = new Set([
  "login expired, please log in again",
  "invalid token",
  "refresh token expired, please log in again",
  "invalid refresh token",
]);
const resizeObserverMessages = new Set([
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
]);

if (isDevelopment && typeof window !== "undefined") {
  const suppressResizeObserverError = (event) => {
    const message = event?.message || event?.reason?.message || "";

    if (resizeObserverMessages.has(message)) {
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
    }
  };

  window.addEventListener("error", suppressResizeObserverError);
  window.addEventListener("unhandledrejection", suppressResizeObserverError);
}

const clearSessionAndRedirect = () => {
  clearSession();
  if (window.location.pathname !== "/auth") {
    toast.error("Your session expired. Please login again.");
    window.location.href = "/auth";
  }
};

axios.interceptors.request.use((config) => {
  const nextConfig = { ...config };
  const token = localStorage.getItem("token");
  const isAuthRoute = typeof nextConfig.url === "string" && nextConfig.url.includes("/auth/");

  if (!nextConfig.skipAuthRefresh && !isAuthRoute && token) {
    nextConfig.headers = {
      ...(nextConfig.headers || {}),
      Authorization: `Bearer ${token}`,
    };
  }

  return nextConfig;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || "";
    const normalizedDetail = String(detail).toLowerCase();
    const refreshToken = localStorage.getItem("refresh_token");
    const isAuthRoute = typeof originalRequest.url === "string" && originalRequest.url.includes("/auth/");
    const isTerminalAuthError = status === 401 && terminalAuthErrors.has(normalizedDetail);
    const shouldAttemptRefresh =
      isTerminalAuthError &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      !isAuthRoute &&
      refreshToken;

    if (!shouldAttemptRefresh) {
      if (isTerminalAuthError && !isAuthRoute) {
        clearSessionAndRedirect();
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = axios.post(
          `${API_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          { skipAuthRefresh: true }
        ).then((response) => {
          setSession({
            token: response.data.token,
            refreshToken: response.data.refresh_token,
            user: response.data.user,
          });
          return response.data.token;
        }).finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      originalRequest.headers = {
        ...(originalRequest.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };
      return axios(originalRequest);
    } catch (refreshError) {
      clearSessionAndRedirect();
      return Promise.reject(refreshError);
    }
  }
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
